import type {
  BillingCycle,
  BillingPackageDto,
  ComparisonPlan,
  ComparisonPlanFeature,
  ComparisonPlanKey,
  PricingQuote,
  PricingQuoteBasePrice,
  PricingQuotePlanBasePrices,
} from '../models/billing.types'

import {
  COMPARISON_PLAN_DEFINITIONS,
  COMPARISON_PLAN_LIMITS,
  type ComparisonPlanDefinition,
} from './comparisonPlanCatalog'

export interface ResolveComparisonPlansOptions {
  billingCycle?: BillingCycle
  quote?: PricingQuote | null
  quoteLoading?: boolean
  quoteError?: boolean
  isShopify?: boolean
}

interface PackageMarketingContent {
  heading: string | null
  features: string[]
  aiHeading: string | null
  aiFeatures: string[]
  aiNote: string
  note: string
}

const RESERVED_SELF_SERVE_PACKAGE_IDS = new Set([4, 11, 10000062])

function feature(key: string, labelKey: string, included = true): ComparisonPlanFeature {
  return { key, label: null, labelKey, included }
}

function normalizePlanKey(value: unknown): string {
  const normalized = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
  return normalized === 'free plan' ? 'free' : normalized
}

function numericPrice(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function currencyCode(value: string | null | undefined, fallback = 'USD'): string {
  const normalized = value?.trim().toUpperCase()
  if (normalized?.length === 3) return normalized
  if (normalized === '€') return 'EUR'
  if (normalized === '£') return 'GBP'
  if (normalized === '₺') return 'TRY'
  return fallback
}

function stringList(value: unknown): string[] {
  return Array.isArray(value)
    ? value
        .map(String)
        .map((item) => item.trim())
        .filter(Boolean)
    : []
}

function isPeriodPriceMap(
  value: PricingQuoteBasePrice | PricingQuotePlanBasePrices | undefined,
): value is PricingQuotePlanBasePrices {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function quotePrice(
  quote: PricingQuote | null,
  planKey: ComparisonPlanKey,
  period: 'monthly' | 'annual',
): number | null {
  const key = Object.keys(quote?.base_prices ?? {}).find(
    (candidate) => normalizePlanKey(candidate) === planKey,
  )
  if (!quote || !key) return null
  const prices = quote.base_prices?.[key]
  const raw = isPeriodPriceMap(prices) ? prices[period] : period === 'monthly' ? prices : null
  return numericPrice(raw)
}

function orderedObjectKeys(value: Record<string, unknown>): string[] {
  const configured = stringList(value.feature_show_order)
  const keys = Object.keys(value).filter((key) => key !== 'feature_show_order')
  return [
    ...configured.filter((key) => keys.includes(key)),
    ...keys.filter((key) => !configured.includes(key)),
  ]
}

function keyFeatures(billingPackage: BillingPackageDto | null): string[] {
  if (!billingPackage?.key_features || typeof billingPackage.key_features !== 'object') return []
  const categories = billingPackage.key_features as Record<string, unknown>
  return orderedObjectKeys(categories).flatMap((categoryName) => {
    const category = categories[categoryName]
    if (!category || typeof category !== 'object') return []
    return orderedObjectKeys(category as Record<string, unknown>).flatMap((featureName) => {
      const item = (category as Record<string, unknown>)[featureName]
      const value =
        item && typeof item === 'object' ? (item as Record<string, unknown>).value : item
      if (value === false || value === null || value === undefined || value === 0 || value === '') {
        return []
      }
      return [value === true ? featureName : `${featureName}: ${String(value)}`]
    })
  })
}

function marketingContent(billingPackage: BillingPackageDto | null): PackageMarketingContent {
  const marketing = billingPackage?.marketing_features
  if (Array.isArray(marketing)) {
    return {
      heading: billingPackage?.previous_tier_name?.trim() || null,
      features: stringList(marketing),
      aiHeading: null,
      aiFeatures: [],
      aiNote: '',
      note: '',
    }
  }
  if (marketing && typeof marketing === 'object') {
    const content = marketing as Record<string, unknown>
    return {
      heading: String(content.heading || billingPackage?.previous_tier_name || '').trim() || null,
      features: stringList(content.features),
      aiHeading: String(content.ai_heading || '').trim() || null,
      aiFeatures: stringList(content.ai_features),
      aiNote: String(content.ai_note || '').trim(),
      note: String(content.note || '').trim(),
    }
  }
  return {
    heading: billingPackage?.previous_tier_name?.trim() || null,
    features: keyFeatures(billingPackage),
    aiHeading: null,
    aiFeatures: [],
    aiNote: '',
    note: '',
  }
}

function isLimitFeature(value: unknown): boolean {
  const label = String(value || '').trim()
  return (
    /\binvites?\b/i.test(label) ||
    /\bmcp\s+users?\b/i.test(label) ||
    /^(?:(?:no|custom|unlimited|\d+)\s+)?users?(?:\s+included)?$/i.test(label)
  )
}

function isAiFeature(value: unknown): boolean {
  return /\b(?:ai|orpheus|impact analysis|credit)\b/i.test(String(value))
}

function cleanProClaim(value: string): string {
  return value
    .replace(/the same four ai tools,?\s*with\s*10\s*x\s*the monthly credits\.?/gi, '')
    .replace(/\b10\s*x\s*the monthly credits\.?/gi, '')
    .trim()
}

function selectPackage(
  packages: readonly BillingPackageDto[],
  key: ComparisonPlanKey,
  cycle: BillingCycle,
  isShopify: boolean,
): BillingPackageDto | null {
  let matches = packages.filter(
    (item) =>
      normalizePlanKey(item.name) === key &&
      (key === 'enterprise' || !RESERVED_SELF_SERVE_PACKAGE_IDS.has(item.id)),
  )
  const storefrontMatches = matches.filter((item) => Boolean(item.is_shopify_package) === isShopify)
  if (storefrontMatches.length > 0) matches = storefrontMatches
  return matches.find((item) => Boolean(item.is_annual) === (cycle === 'yearly')) ?? null
}

function annualMonthlyPrice(
  monthly: number | null,
  annualPackagePrice: number | null,
): number | null {
  if (annualPackagePrice === null) return null
  return monthly !== null && annualPackagePrice > monthly * 2
    ? annualPackagePrice / 12
    : annualPackagePrice
}

function dynamicFeatures(labels: string[], included = true): ComparisonPlanFeature[] {
  return labels.map((label) => ({ key: label, label, labelKey: null, included }))
}

function resolvePlanPrices(
  key: ComparisonPlanKey,
  quote: PricingQuote | null,
  monthlyPackage: BillingPackageDto | null,
  annualPackage: BillingPackageDto | null,
): { monthly: number | null; annual: number | null } {
  if (key === 'free') return { monthly: 0, annual: 0 }
  if (key === 'enterprise') return { monthly: null, annual: null }
  const monthly = quotePrice(quote, key, 'monthly') ?? numericPrice(monthlyPackage?.price)
  const annual =
    quotePrice(quote, key, 'annual') ??
    annualMonthlyPrice(monthly, numericPrice(annualPackage?.price))
  return { monthly, annual }
}

function resolveAiFeatures(
  definition: ComparisonPlanDefinition,
  marketing: PackageMarketingContent,
): ComparisonPlanFeature[] {
  const separated = marketing.features.filter(isAiFeature).filter((item) => !isLimitFeature(item))
  const supplied = marketing.aiFeatures.length > 0 ? marketing.aiFeatures : separated
  if (supplied.length === 0) {
    return definition.aiFeatureKeys.map((key) => feature(key, key, definition.key !== 'free'))
  }
  const labels =
    definition.key === 'pro'
      ? supplied.filter((label) => !/\bcredits?\b/i.test(label) && !/\b10\s*x\b/i.test(label))
      : supplied
  return dynamicFeatures(labels)
}

function aiCredits(key: ComparisonPlanKey, quote: PricingQuote | null): number | null {
  if (key === 'growth') return 50
  return key === 'pro' ? numericPrice(quote?.pro_ai_credits) : null
}

function buildComparisonPlan(
  definition: ComparisonPlanDefinition,
  packages: readonly BillingPackageDto[],
  options: ResolveComparisonPlansOptions,
  cycle: BillingCycle,
  quote: PricingQuote | null,
  quoteCurrency: string,
): ComparisonPlan {
  const isShopify = options.isShopify ?? false
  const monthlyPackage = selectPackage(packages, definition.key, 'monthly', isShopify)
  const annualPackage = selectPackage(packages, definition.key, 'yearly', isShopify)
  const selectedPackage = cycle === 'yearly' ? annualPackage : monthlyPackage
  const marketing = marketingContent(monthlyPackage)
  const prices = resolvePlanPrices(definition.key, quote, monthlyPackage, annualPackage)
  const selectedPrice = cycle === 'yearly' ? prices.annual : prices.monthly
  const isDynamicPaidPlan = definition.key === 'growth' || definition.key === 'pro'

  return {
    ...definition,
    packageId: selectedPackage?.id ?? null,
    packageIds: [monthlyPackage?.id, annualPackage?.id].filter(
      (value): value is number => value !== undefined,
    ),
    name: definition.key === 'free' ? definition.name : (monthlyPackage?.name ?? definition.name),
    price: selectedPrice,
    monthlyPrice: prices.monthly,
    annualMonthlyPrice: prices.annual,
    originalMonthlyPrice: cycle === 'yearly' ? prices.monthly : null,
    annualTotal: cycle === 'yearly' && prices.annual !== null ? prices.annual * 12 : null,
    priceCalculating: isDynamicPaidPlan && selectedPrice === null,
    currencyCode: quote ? quoteCurrency : currencyCode(monthlyPackage?.currency),
    description: monthlyPackage?.description?.trim() || null,
    featuresHeading: marketing.heading,
    features: marketing.features.filter((item) => !isAiFeature(item) && !isLimitFeature(item)),
    limitFeatures: COMPARISON_PLAN_LIMITS[definition.key].map((item) => ({ ...item })),
    aiHeading: definition.key === 'pro' ? null : marketing.aiHeading,
    aiCredits: aiCredits(definition.key, quote),
    aiCreditCalculating:
      definition.key === 'pro' && options.quoteLoading === true && options.quoteError !== true,
    aiFeatures: resolveAiFeatures(definition, marketing),
    aiNote: definition.key === 'pro' ? cleanProClaim(marketing.aiNote) : marketing.aiNote,
    note: definition.key === 'pro' ? cleanProClaim(marketing.note) : marketing.note,
    annual: cycle === 'yearly',
  }
}

export function resolveComparisonPlans(
  packages: readonly BillingPackageDto[],
  options: ResolveComparisonPlansOptions = {},
): ComparisonPlan[] {
  const cycle = options.billingCycle ?? 'monthly'
  const quote = options.quote ?? null
  const quoteCurrency = currencyCode(quote?.currency, 'USD')

  return COMPARISON_PLAN_DEFINITIONS.map((definition) =>
    buildComparisonPlan(definition, packages, options, cycle, quote, quoteCurrency),
  )
}

export function hasCompleteComparisonCatalog(plans: readonly ComparisonPlan[]): boolean {
  return (['growth', 'pro'] as const).every((key) =>
    plans.some((plan) => plan.key === key && plan.packageIds.length > 0),
  )
}

export function comparisonAnnualDiscount(plans: readonly ComparisonPlan[]): number | null {
  for (const plan of plans) {
    if (
      (plan.key === 'growth' || plan.key === 'pro') &&
      plan.monthlyPrice !== null &&
      plan.monthlyPrice > 0 &&
      plan.annualMonthlyPrice !== null
    ) {
      const discount = (1 - plan.annualMonthlyPrice / plan.monthlyPrice) * 100
      if (discount >= 0 && discount < 100) return discount
    }
  }
  return null
}
